import { useRef, type ChangeEvent } from 'react';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

interface ArchiveToolbarProps {
	importing?: boolean;
	exporting?: boolean;
	locked?: boolean;
	lockedReason?: string;
	readonly?: boolean;
	onExportArchive(): Promise<void> | void;
	onImportArchive(file: File): Promise<void> | void;
	onLockedAction?(): void;
}

export function ArchiveToolbar({
	importing = false,
	exporting = false,
	locked = false,
	lockedReason,
	readonly = false,
	onExportArchive,
	onImportArchive,
	onLockedAction,
}: ArchiveToolbarProps) {
	const { t } = useLocale();
	const fileInputRef = useRef<HTMLInputElement>(null);

	function handleChange(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;
		void onImportArchive(file);
	}

	function handleExportClick() {
		if (locked) {
			onLockedAction?.();
			return;
		}

		void onExportArchive();
	}

	function handleImportClick() {
		if (locked) {
			onLockedAction?.();
			return;
		}

		fileInputRef.current?.click();
	}

	const isBusy = importing || exporting;
	const lockedButtonProps = locked ? { 'aria-disabled': true, title: lockedReason } : {};

	return (
		<div className="hud-stack archive-toolbar" role="group" aria-label="Archive transfer" style={{ display: 'grid', gap: 8 }}>
			<button
				className={`hud-subtle-button${locked ? ' is-locked' : ''}`}
				disabled={isBusy}
				onClick={handleExportClick}
				type="button"
				{...lockedButtonProps}
			>
				{exporting ? t('archiveExporting') : t('archiveExport')}
			</button>
			<button
				className={`hud-subtle-button${locked ? ' is-locked' : ''}`}
				disabled={isBusy || (!locked && readonly)}
				onClick={handleImportClick}
				type="button"
				{...lockedButtonProps}
			>
				{importing ? t('archiveImporting') : t('archiveImport')}
			</button>
			<input
				accept=".journeynodes"
				aria-label="Import archive"
				disabled={readonly || locked || isBusy}
				hidden
				onChange={handleChange}
				ref={fileInputRef}
				tabIndex={-1}
				type="file"
			/>
			<p className="hud-hint archive-toolbar__hint">{t('archiveToolbarHint')}</p>
		</div>
	);
}
